import {
  ButtonType,
  ButtonWidget,
  GenericMenuItemInfo
} from '@tableau/widgets';
import { DisplayValueFormatterProps } from '@tableau/widgets/lib/src/widgets/ListMenuDisplayFormatters/DisplayValueFormatter';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import {
  ButtonControlledChildComponentProps,
  ButtonControlledComponent,
  ButtonProps
} from './ButtonControlledComponent';
import { GenericButtonControlledFlyoutListMenu } from './GenericButtonControlledFlyoutListMenu';

export type GenericActionsMenuItem = {
  readonly displayFormatter: React.ComponentClass<DisplayValueFormatterProps>;
  readonly disabled?: boolean;
  readonly execute: () => void;
};

export type GenericActionsMenuProps = {
  readonly actions: ReadonlyArray<GenericActionsMenuItem>;
  readonly buttonLabel: string;
  readonly style?: React.CSSProperties;
};

export class GenericActionsMenu extends React.Component<GenericActionsMenuProps> {

  private readonly memoizedGetGenericActionsMenuButtonComponent = defaultMemoize(getGenericActionsMenuButtonComponent);
  private readonly memoizedGetGenericActionsMenuChildComponent = defaultMemoize(getGenericActionsMenuChildComponent);

  public render(): JSX.Element {
    const button = this.memoizedGetGenericActionsMenuButtonComponent(this.props.children, this.props.style);
    const child = this.memoizedGetGenericActionsMenuChildComponent(this.props.actions);
    return (
      <ButtonControlledComponent
        buttonComponent={button}
        childComponent={child}
        testId={'generic-action-menu-test-id'}
      />
    );
  }

}

type GenericActionsMenuButtonProps = ButtonProps & {
  readonly children: React.ReactNode;
  readonly style?: React.CSSProperties;
};

function GenericActionsMenuButton(props: GenericActionsMenuButtonProps): JSX.Element {
  return (
    <ButtonWidget
      buttonType={ButtonType.Icon}
      handleClick={_.noop}
      children={props.children}
      testId={'generic-action-menu-dropdown-button'}
      style={props.style}
    />
  );
}

function getGenericActionsMenuButtonComponent(
  children: React.ReactNode,
  style?: React.CSSProperties
): React.ComponentType<ButtonProps> {
  return function actionsMenuButton(buttonProps: ButtonProps): JSX.Element {
    const fullProps: GenericActionsMenuButtonProps = { ...buttonProps, children, style };
    return <GenericActionsMenuButton {...fullProps} />;
  };
}

function getGenericActionsMenuChildComponent(
  actions: ReadonlyArray<GenericActionsMenuItem>
): React.ComponentType<ButtonControlledChildComponentProps> {
  const menuItems: GenericMenuItemInfo[] = actions.map((action, index) => ({
    disabled: action.disabled,
    displayFormatterBoundWithData: action.displayFormatter,
    value: `${index}`
  }));
  const itemSelected = (value: string) => {
    const index = parseInt(value, 10);
    const action = actions[index];
    if (action) {
      action.execute();
    }
  };
  return function actionsMenuChild(props: ButtonControlledChildComponentProps): JSX.Element {
    return <GenericButtonControlledFlyoutListMenu
      onComponentDidMount={_.noop}
      menuItems={menuItems}
      onMenuItemSelected={itemSelected}
      anchorElement={props.anchorElement}
      closeMe={props.closeMe}
      testId={'generic-action-menu-wrapper'}
    />;
  };
}
